import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../components/Auth/AuthContext/AuthContext';

interface UserInactivityProps {
  isDialogOpen: boolean;
  dialogCountdown: number;
  refreshUserSession: () => Promise<void>;
  logoutUser: () => void;
}

type InactivityEvent =
  { type: 'ACTIVITY' } | { type: 'OPEN_DIALOG'; expiresAt: number } | { type: 'CLOSE_DIALOG' } | { type: 'LOGOUT' };

function useUserInactivity(): UserInactivityProps {
  const { getAccessTokenSilently, logout, isAuthenticated } = useAuth();

  const IDLE_TIME = 14 * 60 * 1000;
  const DIALOG_COUNTDOWN = 60 * 1000;

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [dialogCountdown, setDialogCountdown] = useState(Math.ceil(DIALOG_COUNTDOWN / 1000));

  const lastActivityRef = useRef<number>(0);
  const userInactivityTimerRef = useRef<number | null>(null);
  const dialogCounterRef = useRef<number | null>(null);
  const channelRef = useRef<BroadcastChannel | null>(null);

  useEffect(() => {
    lastActivityRef.current = Date.now();
  }, []);

  const stopTimer = () => {
    if (dialogCounterRef.current !== null) {
      clearInterval(dialogCounterRef.current);
      dialogCounterRef.current = null;
    }
  };

  // BroadcastChannel
  useEffect(() => {
    if (!isAuthenticated) return;

    const channel = new BroadcastChannel('user-inactivity');
    channelRef.current = channel;

    channel.onmessage = (event: MessageEvent<InactivityEvent>) => {
      const message = event.data;

      switch (message.type) {
        case 'ACTIVITY':
          lastActivityRef.current = Date.now();
          break;

        case 'OPEN_DIALOG':
          userInactivityTimerRef.current = message.expiresAt;
          setIsDialogOpen(true);
          break;

        case 'CLOSE_DIALOG':
          userInactivityTimerRef.current = null;
          setIsDialogOpen(false);
          setDialogCountdown(Math.ceil(DIALOG_COUNTDOWN / 1000));
          break;

        case 'LOGOUT':
          userInactivityTimerRef.current = null;
          setIsDialogOpen(false);
          stopTimer();
          logout({ logoutParams: { returnTo: window.location.origin } });
          break;
      }
    };

    return () => {
      channel.close();
      channelRef.current = null;
    };
  }, [isAuthenticated, logout, DIALOG_COUNTDOWN]);

  const trackUserActivity = useCallback(() => {
    if (isDialogOpen) return;

    lastActivityRef.current = Date.now();
    channelRef.current?.postMessage({ type: 'ACTIVITY' });
  }, [isDialogOpen]);

  // User activity tracking
  useEffect(() => {
    if (!isAuthenticated) return;

    const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll'];
    const onUserActivity = () => trackUserActivity();

    events.forEach((e) => window.addEventListener(e, onUserActivity, { passive: true }));

    return () => {
      events.forEach((e) => window.removeEventListener(e, onUserActivity));
    };
  }, [isAuthenticated, trackUserActivity]);

  // Inactivity timer
  useEffect(() => {
    if (!isAuthenticated) return;
    if (dialogCounterRef.current !== null) return;

    dialogCounterRef.current = window.setInterval(() => {
      const now = Date.now();

      if (!isDialogOpen) {
        const inactiveFor = now - lastActivityRef.current;

        if (inactiveFor >= IDLE_TIME) {
          const expirationTime = now + DIALOG_COUNTDOWN;

          userInactivityTimerRef.current = expirationTime;
          setIsDialogOpen(true);
          setDialogCountdown(Math.ceil(DIALOG_COUNTDOWN / 1000));

          channelRef.current?.postMessage({
            type: 'OPEN_DIALOG',
            expiresAt: expirationTime,
          });
        }
      } else {
        if (!userInactivityTimerRef.current) return;

        const remainingMs = userInactivityTimerRef.current - now;
        const remainingSeconds = Math.ceil(remainingMs / 1000);

        if (remainingSeconds <= 0) {
          channelRef.current?.postMessage({ type: 'LOGOUT' });

          userInactivityTimerRef.current = null;
          setIsDialogOpen(false);
          setDialogCountdown(0);
          stopTimer();

          logout({ logoutParams: { returnTo: window.location.origin } });
        } else {
          setDialogCountdown(remainingSeconds);
        }
      }
    }, 1000);

    return stopTimer;
  }, [isAuthenticated, isDialogOpen, IDLE_TIME, DIALOG_COUNTDOWN, logout]);

  const refreshUserSession = useCallback(async () => {
    try {
      await getAccessTokenSilently();
    } catch {
      channelRef.current?.postMessage({ type: 'LOGOUT' });
      logout({ logoutParams: { returnTo: window.location.origin } });
      return;
    }

    lastActivityRef.current = Date.now();
    userInactivityTimerRef.current = null;
    setIsDialogOpen(false);
    setDialogCountdown(Math.ceil(DIALOG_COUNTDOWN / 1000));

    channelRef.current?.postMessage({ type: 'CLOSE_DIALOG' });
  }, [getAccessTokenSilently, logout, DIALOG_COUNTDOWN]);

  const logoutUser = useCallback(() => {
    channelRef.current?.postMessage({ type: 'LOGOUT' });

    userInactivityTimerRef.current = null;
    setIsDialogOpen(false);
    logout({ logoutParams: { returnTo: window.location.origin } });
  }, [logout]);

  return { isDialogOpen, dialogCountdown, refreshUserSession, logoutUser };
}

export default useUserInactivity;
